import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import srOnly from '@helpers/srOnly';
import styled from 'styled-components';
import ButtonStyles from '../../Buttons/Button/Button.styles';
import CheckboxListStyles from '../../Misc/Forms/Inputs/CheckboxList/CheckboxList.styles';
const Container = styled.section `
  --backHeight: 52px;

  @media ${from(Device.DesktopSmall)} {
    position: relative;
  }
`;
const Button = styled.button `
  ${ButtonReset};
  ${fonts.lato.bold};

  font-size: 18px;
  line-height: 26px;

  &:focus-visible {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
  }

  > span {
    ${srOnly};
  }

  @media ${until(Device.DesktopSmall)} {
    svg {
      display: none;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    display: flex;
    font-weight: 400;
    gap: 24px;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 18px;
    border-radius: 12px;
    border: 1px solid ${brand.primary.purple.light};
    height: var(--itemHeight, 48px);

    svg {
      ${transition('transform')};

      width: 24px;
      height: 24px;
    }

    &[aria-expanded='true'] {
      svg {
        transform: rotate(180deg);
      }
    }
  }
`;
const Options = styled.div `
  ${transition('opacity, transform', 0.5)};

  position: fixed;
  top: calc(var(--backHeight) + 10px);
  right: 0;
  height: calc(100% - var(--backHeight) - var(--applyHeight, 0px) - 10px);
  padding: 16px 20px 20px;
  width: 100%;
  max-width: 767px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 22px;
  z-index: ${Layers.Navigation + 2};
  background-color: ${brand.global.white};

  ${Container}:has(${Button}[aria-expanded='false']) & {
    opacity: 0;
    transform: translateX(100%);
    pointer-events: none;
  }

  ${CheckboxListStyles.Label} {
    --borderRadius: 2px;
    --boxSize: 24px;

    font-size: 16px;
  }

  @media ${until(Device.DesktopSmall)} {
    &::before {
      content: '';
      position: absolute;
      background-color: ${brand.grey.mid};
      width: calc(100% - 40px);
      height: 1px;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
    }

    ${CheckboxListStyles.Label} {
      --textColour: ${brand.grey.dark};
      --borderColour: ${brand.grey.dark};
    }

    ${CheckboxListStyles.Option}:nth-child(2) {
      position: absolute;
      top: 16px;
      right: 20px;

      ${CheckboxListStyles.Label}::after {
        display: none;
      }
    }

    ${CheckboxListStyles.Label} {
      padding: 0 calc(var(--boxSize) + ${fluid(10, 12)}) 0 0;
      width: 100%;
    }

    ${CheckboxListStyles.Label}::before, ${CheckboxListStyles.Label}::after {
      left: auto;
      right: 0;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    ${scrollbars};

    position: absolute;
    top: calc(100% + 16px);
    left: 0;
    max-height: 600px;
    height: min-content;
    width: min-content;
    background-color: ${brand.primary.grey};
    box-shadow: 0px 4px 13px 0px rgba(0, 0, 0, 0.25);
    padding: 12px;
    border-radius: 10px;
    min-width: 252px;

    ${Container}:has(${Button}[aria-expanded='false']) & {
      opacity: 0;
      transform: translateY(-20px);
      pointer-events: none;
    }
  }
`;
const MobileLabel = styled.div `
  ${fonts.lato.bold};

  font-size: 18px;
  line-height: 26px;
  margin-bottom: 4px;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const BackButton = styled.button `
  ${ButtonReset};
  ${transition('opacity, transform', 0.5)};
  ${fonts.lato.bold};

  position: fixed;
  top: 0;
  right: 0;
  height: var(--backHeight);
  background: ${brand.global.white};
  padding: 11px var(--sitePadding, 16px) 0;
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 18px;
  width: 100%;
  max-width: 767px;
  z-index: ${Layers.Navigation + 2};

  ${Container}:has(${Button}[aria-expanded='false']) & {
    opacity: 0;
    transform: translateX(100%);
  }

  > svg {
    display: block;
    width: 20px;
    transform: scale(-1);
  }

  ${ButtonStyles.Container} {
    --buttonHeight: 36px;

    margin-left: auto;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const DropdownFilterStyles = {
    Container,
    Button,
    Options,
    BackButton,
    MobileLabel,
};
export default DropdownFilterStyles;
